// The icon set: a 16 grid, 1.25 px lines, currentColor, so both themes and
// every state (hover, selected) color them through the text color.
// Paths come from the approved icon study of the old web; parts add their own
// shapes the same way (16 grid, same stroke).

import type { ComponentType } from "react";
import { useId } from "react";

/**
 * The question mark without its circle (its dot: QUESTION_DOT), for a mark
 * that draws the circle its own way (a chat asking while it works).
 */
export const QUESTION_HOOK =
  "M6.3 6.4a1.75 1.75 0 113.1 1.1c-.6.5-1.4.8-1.4 1.8";
export const QUESTION_DOT = { cx: 8, cy: 11.4, r: 0.7 } as const;

const paths = {
  inbox: "M2 9l2-6h8l2 6v4H2z M2 9h3l1 2h4l1-2h3",
  cases: "M2 5h12v8H2z M5 5V3h6v2 M2 8h12 M6 8v2h4V8",
  // A move of a Case: an arrow to the right in a circle (move it forward).
  action: "M8 2a6 6 0 110 12A6 6 0 018 2z M7 5.5L9.5 8 7 10.5",
  // Someone else's turn: an hourglass.
  waiting:
    "M3.5 2h9 M3.5 14h9 M4.5 2v2.5L8 8l3.5-3.5V2 M4.5 14v-2.5L8 8l3.5 3.5V14",
  // Waiting for its turn (a chat whose agent has not started yet): a clock,
  // not the hourglass of someone else's turn.
  clock: "M8 2a6 6 0 110 12A6 6 0 018 2z M8 5v3.2l2.2 1.4",
  // A label: a tag with its hole (drawn below as a circle).
  label: "M2.5 3.5v4l6 6 5-5-6-6h-4a1 1 0 00-1 1z",
  // A day (due dates, plans): a calendar sheet.
  calendar:
    "M3 3.5h10a1 1 0 011 1v8a1 1 0 01-1 1H3a1 1 0 01-1-1v-8a1 1 0 011-1z M5 2v3 M11 2v3 M2 7h12",
  // A Case's Chain as a diagram: a line with a branch leaving at 45 degrees,
  // and its stations (drawn below as circles).
  chain: "M5 4.5v7 M5 6.5l4.4 4.4",
  // A file attached to something: a paper clip.
  attach:
    "M10.5 5.5l-4.8 4.8a1.5 1.5 0 002.1 2.1l5-5a3 3 0 00-4.2-4.2l-5 5a4.5 4.5 0 006.4 6.4l4.3-4.3",
  people:
    "M6 7.75a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z M1.75 13.5c0-2.5 1.9-4 4.25-4s4.25 1.5 4.25 4 M11 7a2 2 0 100-4 2 2 0 000 4z M11.75 9.5c1.5.25 2.5 1.6 2.5 3.5",
  person:
    "M8 7.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z M3 14c0-2.8 2.2-4.5 5-4.5s5 1.7 5 4.5",
  agent:
    "M3 3h10a1 1 0 011 1v8a1 1 0 01-1 1H3a1 1 0 01-1-1V4a1 1 0 011-1z M4.5 6l2 2-2 2 M9 10h2.5",
  // One chat with an agent: a speech bubble with a prompt in it (the rows of
  // the Agents list, references to a chat). The section itself is `agent`.
  agentChat:
    "M3 2.5h10a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 01-1.5 1.5H7.5L4.5 14v-2.5H3A1.5 1.5 0 011.5 10V4A1.5 1.5 0 013 2.5z M4.75 5.25 6.5 7 4.75 8.75 M8.25 8.75h3",
  // A child chat started from another one: an arrow turning right.
  child: "M5 3.5v4.75a2 2 0 002 2h5.5 M10 7.75l2.5 2.5-2.5 2.5",
  documents:
    "M9 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V6z M9 2v4h4 M5.5 9h5 M5.5 11.5H9",
  favorite: "M8 2l1.8 3.8 4.2.6-3 3 .7 4.2L8 11.7l-3.7 1.9.7-4.2-3-3 4.2-.6z",
  "folder-closed":
    "M2 4a1 1 0 011-1h3l2 2h5a1 1 0 011 1v6a1 1 0 01-1 1H3a1 1 0 01-1-1z",
  "folder-open": "M2 13V4a1 1 0 011-1h3l2 2h4a1 1 0 011 1 M2 13l2-6h10l-2 6z",
  search: "M10.5 10.5l3.5 3.5",
  panel:
    "M3 3h10a1 1 0 011 1v8a1 1 0 01-1 1H3a1 1 0 01-1-1V4a1 1 0 011-1z M6 3v10 M4 5.5v1",
  plus: "M8 3v10 M3 8h10",
  // Bars over a base line: a look back over a span (TimeBlocker's review).
  review: "M2.5 13.5h11 M4.5 11V8 M8 11V4.5 M11.5 11V6.5",
  // Less (zooming out), beside plus.
  minus: "M3 8h10",
  link: "M7 9a2.5 2.5 0 003.5 0l2.5-2.5a2.5 2.5 0 00-3.5-3.5L8.3 4.2 M9 7a2.5 2.5 0 00-3.5 0L3 9.5A2.5 2.5 0 006.5 13l1.2-1.2",
  // A plug: what an app is plugged in with (the MCP clients, the connected apps).
  plug: "M6 1.75v3.5 M10 1.75v3.5 M3.75 5.25h8.5v2.5a3.25 3.25 0 01-3.25 3.25H7a3.25 3.25 0 01-3.25-3.25z M8 11v3.25",
  // A lock with its keyhole (a dot over a short slot).
  lock: "M4 7h8v6H4z M5.5 7V5a2.5 2.5 0 015 0v2 M8 10v1.25",
  // The same lock open: its shackle lifted off the right side.
  unlock: "M4 7h8v6H4z M5.5 7V5a2.5 2.5 0 014.9-.7 M8 10v1.25",
  // A cloud: where data kept by the cloud lives.
  cloud:
    "M4.75 12.5a2.75 2.75 0 01-.4-5.47 3.75 3.75 0 017.3-.9 2.75 2.75 0 01-.4 6.37z",
  updown: "M5.5 6L8 3.5 10.5 6 M5.5 10L8 12.5 10.5 10",
  settings:
    "M8 1.8v1.6M8 12.6v1.6M14.2 8h-1.6M3.4 8H1.8M12.4 3.6l-1.1 1.1M4.7 11.3l-1.1 1.1M12.4 12.4l-1.1-1.1M4.7 4.7L3.6 3.6",
  close: "M4 4l8 8 M12 4l-8 8",
  // The window's own buttons (Windows, Linux): drawn finer than the rest.
  "window-minimize": "M4 8h8",
  "window-maximize": "M4.5 4.5h7v7h-7z",
  "window-restore": "M4.5 6.5h5v5h-5z M6.5 6.5v-2h5v5h-2",
  check: "M3.5 8.5l3 3 6-7",
  back: "M10 3.5L5.5 8l4.5 4.5",
  // The next one (the next day): the back chevron turned around.
  forward: "M6 3.5L10.5 8 6 12.5",
  // TimeBlocker: a day's sheet (its two rings) with two blocks, the lower one
  // set off to the right.
  timeblocker:
    "M2.5 3.5h11v10h-11z M2.5 6.5h11 M5.5 2v3 M10.5 2v3 M5 9h4 M7.5 11.5h3.5",
  // Start and stop working: filled shapes (drawn below).
  play: "",
  stop: "",
  // Up one level (to the parent), apart from back in history: an arrow
  // rising from the right, then up.
  up: "M3 6.5L6.5 3 10 6.5 M6.5 3v6.5a3 3 0 003 3H13",
  keyboard:
    "M2.5 4h11a1 1 0 011 1v6a1 1 0 01-1 1h-11a1 1 0 01-1-1V5a1 1 0 011-1z M4.5 6.5h1 M7.5 6.5h1 M10.5 6.5h1 M5 9.5h6",
  more: "",
  // Copy: two sheets.
  copy: "M7.1 5.5h4.8a1.6 1.6 0 011.6 1.6v4.8a1.6 1.6 0 01-1.6 1.6H7.1a1.6 1.6 0 01-1.6-1.6V7.1a1.6 1.6 0 011.6-1.6z M10.5 3.2V3.1c0-.9-.7-1.6-1.6-1.6H4.1c-.9 0-1.6.7-1.6 1.6v4.8c0 .9.7 1.6 1.6 1.6h.2",
  // Down to the end of a line of messages.
  down: "M8 3v10 M3.5 8.5L8 13l4.5-4.5",
  // Going back to a point (rewind, undo): an arrow turning back to the left.
  rewind: "M5.5 3 2.5 6l3 3 M2.5 6h7a3.5 3.5 0 010 7H6.5",
  // Going forward again after an undo: the same arrow turning to the right.
  redo: "M10.5 3l3 3-3 3 M13.5 6h-7a3.5 3.5 0 000 7h3",
  // Waiting for your answer: a question mark in a circle (its dot below).
  question: `M8 2a6 6 0 110 12A6 6 0 018 2z ${QUESTION_HOOK}`,
  // A side chat off a reply: a second speech bubble behind the first.
  sideChat:
    "M3.2 5.5h5.6a1.7 1.7 0 011.7 1.7v2.9a1.7 1.7 0 01-1.7 1.7H5.6l-2.5 2.1v-2.1a1.7 1.7 0 01-1.6-1.7V7.2a1.7 1.7 0 011.7-1.7z M5.5 5.5V3.7A1.7 1.7 0 017.2 2h5.6a1.7 1.7 0 011.7 1.7v2.9a1.7 1.7 0 01-1.7 1.7v1.8l-2.1-1.8",
  // Open what a side view shows on its own screen: two corners pulled apart.
  expand: "M9.5 2.5h4v4 M13.5 2.5L9.5 6.5 M6.5 13.5h-4v-4 M2.5 13.5l4-4",
  // Open on its own page: a frame with an arrow leaving its top right.
  open: "M7 3H4.5A1.5 1.5 0 003 4.5v7A1.5 1.5 0 004.5 13h7a1.5 1.5 0 001.5-1.5V9 M9.5 2.5h4v4 M13.5 2.5L8 8",
  // Out on the web (published by link): a globe.
  // Out of reach (the cloud could not be read): a little dinosaur, as a
  // browser shows when it is offline. Its eye is drawn below.
  unreachable:
    "M8.5 7.5V2.5a.5.5 0 01.5-.5h4a.5.5 0 01.5.5V5h-2.5 M11 5.5v1h1.5 M11 6.5V10l-1.5 1.5V14 M9.5 11.5H6.5V14 M6.5 11.5C4.5 11 3 9.5 2.5 7 M2.5 7c1.5 1.3 3.5 2 6 .5",
  globe:
    "M8 2a6 6 0 110 12A6 6 0 018 2z M2 8h12 M8 2c1.7 1.6 2.5 3.6 2.5 6S9.7 12.4 8 14 M8 2C6.3 3.6 5.5 5.6 5.5 8s.8 4.4 2.5 6",
  // A conversation between people: a speech bubble.
  talk: "M3 3h10a1 1 0 011 1v6a1 1 0 01-1 1H7l-3 2.5V11H3a1 1 0 01-1-1V4a1 1 0 011-1z",
  // A direct message: two bubbles facing each other.
  direct:
    "M2.5 3h7a1 1 0 011 1v4a1 1 0 01-1 1H5.5L3.5 10.5V9h-1a1 1 0 01-1-1V4a1 1 0 011-1z M12.5 6h1a1 1 0 011 1v4a1 1 0 01-1 1h-1v1.5L10.5 12H8a1 1 0 01-1-1",
  // An announcement: a megaphone.
  broadcast: "M2.5 6.5v3h2l5 3v-9l-5 3z M11.5 6a2.5 2.5 0 010 4 M5 9.5l.8 3.5",
  /** A call (the old web's Voices). */
  voice: "M2 7v2 M5 4.5v7 M8 2v12 M11 5v6 M14 7v2",
  // Adding a reaction: a face.
  smile:
    "M8 2a6 6 0 110 12A6 6 0 018 2z M5.5 9.5a3 3 0 005 0 M6 6.5v.2 M10 6.5v.2",
  // Keeping a thing at the top: a pin.
  pin: "M6 2.5h4 M7 2.5v4L4.5 9h7L9 6.5v-4 M8 9v4.5",
  // Seen: an eye.
  eye: "M1.8 8S4 3.8 8 3.8 14.2 8 14.2 8 12 12.2 8 12.2 1.8 8 1.8 8z M8 6.2a1.8 1.8 0 110 3.6 1.8 1.8 0 010-3.6z",
  // A team's tasks (the old web's sidebar marks): a checked box, a flag, the
  // turning arrows of a cycle, and a calendar with what comes next.
  issues:
    "M4 2.5h8a1.5 1.5 0 011.5 1.5v8a1.5 1.5 0 01-1.5 1.5H4A1.5 1.5 0 012.5 12V4A1.5 1.5 0 014 2.5z M5 8l2 2 4-4",
  projects: "M3 14V3c3-3 6 3 10 0v7c-4 3-7-3-10 0",
  cycles:
    "M13 6a5.2 5.2 0 00-9-2L2 6 M2 2.5V6h3.5 M3 10a5.2 5.2 0 009 2l2-2 M14 13.5V10h-3.5",
  upcoming:
    "M3 3.5h10a1 1 0 011 1v8a1 1 0 01-1 1H3a1 1 0 01-1-1v-8a1 1 0 011-1z M5 2v3 M11 2v3 M2 7h12 M7 9l2 1.5L7 12",
  // Settings items (the settings sidebar folds to these). The usual choices:
  // two sliders (general), a half-filled circle (appearance), a phone, a bell
  // (notifications), a pulse (status), a window with its menu bar, a prompt
  // (command line), a switch (features), "#" (spaces), a person with a plus
  // (invites).
  sliders: "M2.5 5h4 M9.5 5h4 M2.5 11h7 M12.5 11h1",
  appearance: "M8 2a6 6 0 110 12A6 6 0 018 2z",
  phone:
    "M5 1.75h6a1.25 1.25 0 011.25 1.25v10a1.25 1.25 0 01-1.25 1.25H5A1.25 1.25 0 013.75 13V3A1.25 1.25 0 015 1.75z M7 12h2",
  bell: "M4 10.5V7a4 4 0 018 0v3.5l1 1.5H3z M6.75 13.5a1.25 1.25 0 002.5 0",
  status: "M1.5 8.5h3l1.75-4 3.5 8 1.75-4h3",
  menubar:
    "M2.5 3h11a1 1 0 011 1v8a1 1 0 01-1 1h-11a1 1 0 01-1-1V4a1 1 0 011-1z M1.5 6h13 M10 4.5h2",
  terminal: "M3 4.5L6.5 8 3 11.5 M8.5 11.5h4.5",
  toggle: "M5.5 4.5h5a3.5 3.5 0 010 7h-5a3.5 3.5 0 010-7z",
  space: "M6.5 2.5L5 13.5 M11 2.5L9.5 13.5 M3 6h10.5 M2.5 10h10.5",
  // A workspace, any one: layers stacked, one of several.
  workspace:
    "M8 2.5l5.5 2.75L8 8 2.5 5.25z M2.5 8L8 10.75 13.5 8 M2.5 10.75L8 13.5l5.5-2.75",
  invite:
    "M7 7.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z M2 14c0-2.8 2.2-4.5 5-4.5 1 0 1.9.2 2.6.6 M12.5 9.5v4 M10.5 11.5h4",
  // A backup: an archive box with its lid and handle.
  archive: "M2 3h12v3H2z M3 6v7h10V6 M6.5 8.5h3",
  // A PC: a laptop's screen over its base.
  pc: "M3.5 3.5h9a1 1 0 011 1V11h-11V4.5a1 1 0 011-1z M1.5 12.5h13",
  // Devices together (the device list): a laptop with a phone in front of
  // its lower right corner, the laptop's lines stopping short of the phone.
  devices:
    "M8.25 10.25H3.5a1 1 0 01-1-1V4a1 1 0 011-1h7.5a1 1 0 011 1v.75 M1 12.25h7.25 M10.75 6h2.5a1 1 0 011 1v6a1 1 0 01-1 1h-2.5a1 1 0 01-1-1V7a1 1 0 011-1z",
  // A dataset: rows kept together, as a database's cylinder with two bands.
  dataset:
    "M3 4c0-1.1 2.2-2 5-2s5 .9 5 2-2.2 2-5 2-5-.9-5-2z M3 4v8c0 1.1 2.2 2 5 2s5-.9 5-2V4 M3 8c0 1.1 2.2 2 5 2s5-.9 5-2",
  // Signing out: a door's frame with an arrow leaving it.
  signOut: "M6.5 2.5h-3a1 1 0 00-1 1v9a1 1 0 001 1h3 M10.5 5l3 3-3 3 M13.5 8H6",
} as const;

export type IconKind = keyof typeof paths;

/** Whether a name (from the outside's data) is one of the set. */
export const isIconKind = (name: unknown): name is IconKind =>
  typeof name === "string" && Object.hasOwn(paths, name);

export function Icon({
  kind,
  className = "",
  size = 16,
  stroke,
}: {
  kind: IconKind;
  className?: string;
  size?: number;
  /** The line width on the 16 grid (a few marks were drawn a touch finer or bolder). */
  stroke?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke ?? 1.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`pointer-events-none flex-none ${className}`}
    >
      {paths[kind] && <path d={paths[kind]} />}
      {kind === "search" && <circle cx="7" cy="7" r="4.5" />}
      {kind === "label" && <circle cx="5.25" cy="5.75" r="0.9" />}
      {(kind === "lock" || kind === "unlock") && (
        <circle cx="8" cy="9.6" r="0.9" fill="currentColor" stroke="none" />
      )}
      {kind === "unreachable" && (
        <circle cx="10.2" cy="3.4" r="0.55" fill="currentColor" stroke="none" />
      )}
      {kind === "chain" && (
        <>
          <circle cx="5" cy="3" r="1.5" />
          <circle cx="5" cy="13" r="1.5" />
          <circle cx="10.5" cy="12" r="1.5" />
        </>
      )}
      {kind === "question" && (
        <circle {...QUESTION_DOT} fill="currentColor" stroke="none" />
      )}
      {kind === "settings" && (
        <>
          <circle cx="8" cy="8" r="2.1" />
          <circle cx="8" cy="8" r="4.6" />
        </>
      )}
      {kind === "sliders" && (
        <>
          <circle cx="8" cy="5" r="1.5" />
          <circle cx="11" cy="11" r="1.5" />
        </>
      )}
      {kind === "appearance" && (
        <path d="M8 2a6 6 0 000 12z" fill="currentColor" stroke="none" />
      )}
      {kind === "toggle" && <circle cx="10.5" cy="8" r="1.75" />}
      {kind === "play" && (
        <path d="M5 3.5v9l7.5-4.5z" fill="currentColor" stroke="none" />
      )}
      {kind === "stop" && (
        <rect
          x="4"
          y="4"
          width="8"
          height="8"
          rx="1"
          fill="currentColor"
          stroke="none"
        />
      )}
      {kind === "more" && (
        <g fill="currentColor" stroke="none">
          <circle cx="3.5" cy="8" r="1.1" />
          <circle cx="8" cy="8" r="1.1" />
          <circle cx="12.5" cy="8" r="1.1" />
        </g>
      )}
    </svg>
  );
}

/**
 * Makes an icon component of a kind, for places that take a component (nav
 * items). It keeps the kind as `iconName`, so the outside's data can name it.
 */
export const iconOf = (kind: IconKind) =>
  Object.assign(
    function KindIcon({ className }: { className?: string }) {
      return <Icon kind={kind} className={className} />;
    },
    { iconName: kind },
  );

/**
 * An icon component by its name in the outside's data (contract.ts
 * `ShellIconName`): the kind of that name, or a plain mark for a name this
 * app does not know (a newer cloud's).
 */
export const iconByName = (
  name: string | undefined,
): ComponentType<{ className?: string }> =>
  name && name in paths ? iconOf(name as IconKind) : iconOf("status");

/** The name of an icon component made by `iconOf`, or undefined for another. */
export const iconNameOf = (icon: unknown): string | undefined => {
  const name = (icon as { iconName?: unknown } | null)?.iconName;
  return typeof name === "string" ? name : undefined;
};

/**
 * The trameria mark (tiger's eye). Has no ground color, so one drawing fits both themes.
 * A host may recolor it through `--brand-mark-1..4` (default: the navy one).
 */
export function BrandMark({
  size = 36,
  current = false,
}: {
  size?: number;
  /** The place shown now: cut squarer, like a workspace's tile. */
  current?: boolean;
}) {
  // One id per mark: two marks on a page must not share a clip.
  const clip = `trameria-mark-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <svg
      viewBox="0 0 32 32"
      width={size}
      height={size}
      aria-hidden="true"
      className="flex-none"
    >
      <defs>
        <clipPath id={clip}>
          {/* A rect with full corners is the circle; current, the corners
              shrink to the app icon's. The attribute, not CSS `rx`: WebKit
              ignores the CSS one inside a clipPath. */}
          <rect x="2" y="2" width="28" height="28" rx={current ? 8.4 : 14} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clip})`}>
        <rect
          x="2"
          y="2"
          width="28"
          height="28"
          fill="var(--brand-mark-1, #204e6f)"
        />
        <circle
          cx="38"
          cy="19"
          r="28.3591"
          fill="var(--brand-mark-2, #c2e1e5)"
        />
        <circle cx="44" cy="16" r="31.8" fill="var(--brand-mark-3, #2e6078)" />
        <circle cx="38" cy="16" r="18.2" fill="var(--brand-mark-2, #c2e1e5)" />
        <circle cx="44" cy="16" r="21.8" fill="var(--brand-mark-4, #5f8fa1)" />
      </g>
    </svg>
  );
}

/**
 * The open/closed triangle: one shape rotated, in a fixed box, so nothing
 * shifts. It grows with the text beside it: about 7×8 px next to 12 px text,
 * the size of the old "▸" drawn 1.3 times (a smaller one was hard to see and
 * to aim at).
 * Its box is centered on the text's line only in a flex row (`items-center`);
 * a wrapper around it must be a flex box too, or it sits on the baseline,
 * about 2px above the text's middle.
 */
export function Caret({ open }: { open: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="inline-flex w-[1em] flex-none items-center justify-center transition-transform duration-150"
      style={open ? { transform: "rotate(90deg)" } : undefined}
    >
      <svg width="1em" height="1em" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M3 2L10 6 3 10z" fill="currentColor" />
      </svg>
    </span>
  );
}
